import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, MessageSquareQuote, Users, Mic, ArrowRight, PlayCircle, Search as SearchIcon } from "lucide-react";
import api from "@/lib/api";
import useSeo from "@/hooks/useSeo";
import NewsCard, { CategoryBadge } from "@/components/NewsCard";
import SectionHeader from "@/components/SectionHeader";
import { LogoMark } from "@/components/Logo";
import { CATEGORY_META, formatDate } from "@/lib/constants";

const VALUES = [
  { icon: ShieldCheck, title: "Independencia editorial", text: "Decimos lo que otros callan, sin responder a intereses." },
  { icon: MessageSquareQuote, title: "Rigor periodístico", text: "Investigamos, contrastamos y contamos con responsabilidad." },
  { icon: Users, title: "Pluralidad", text: "Todas las voces, todas las miradas." },
  { icon: Mic, title: "Damos voz", text: "A las comunidades, a las historias, a las personas." },
];

function Skeleton() {
  return <div className="max-w-container mx-auto px-4 py-20 animate-pulse">
    <div className="h-[420px] bg-gray-100 rounded-md mb-8" />
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">{[1, 2, 3].map((i) => <div key={i} className="h-64 bg-gray-100 rounded" />)}</div>
  </div>;
}

export default function Home() {
  const [data, setData] = useState(null);
  useSeo({ title: "Historias que nos conectan", description: "NEXODUM · Medio de comunicación independiente. Periodismo, actualidad, culturas, sociedad, mundo e investigación." });

  useEffect(() => { api.get("/home").then((r) => setData(r.data)).catch(() => setData({})); }, []);

  if (!data) return <Skeleton />;
  const { hero, secondary = [], latest = [], voces = [], investiga = [] } = data;

  return (
    <div data-testid="home-page">
      {/* Brand banner hero strip */}
      <section className="bg-nx-dark text-white overflow-hidden relative border-b border-white/10">
        <div className="absolute inset-0 opacity-20" style={{ background: "radial-gradient(circle at 80% 30%, #0055FF 0%, transparent 45%), radial-gradient(circle at 20% 70%, #FF6B35 0%, transparent 40%)" }} />
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14 relative">
          <div className="flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
            <div className="flex items-center gap-4">
              <LogoMark size={64} />
              <div>
                <h1 className="font-sans font-black text-3xl md:text-5xl tracking-tight">NEX<span className="nx-gradient-text">O</span>DUM</h1>
                <p className="text-slate-300 tracking-[0.25em] text-[10px] md:text-xs uppercase mt-1">Historias que nos conectan</p>
              </div>
            </div>
            <div className="md:border-l md:border-white/15 md:pl-10 flex-1">
              <p className="text-sm md:text-base text-slate-300 max-w-2xl leading-relaxed">
                Medio de comunicación independiente dedicado a informar, investigar y producir contenidos sobre actualidad, culturas, comunidades, sociedad, historia, migraciones y acontecimientos internacionales.
              </p>
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] uppercase tracking-widest text-slate-400">
                <span>Periodismo</span><span className="text-nx-orange">·</span><span>Actualidad</span>
                <span className="text-nx-blue">·</span><span>Diversidad</span><span className="text-nx-violet">·</span><span>Libertad de expresión</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Portada bento */}
      <section className="max-w-container mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8">{hero && <NewsCard article={hero} variant="hero" />}</div>
          <aside className="lg:col-span-4">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-1.5 h-5 bg-nx-orange rounded-sm" />
              <h2 className="font-serif font-black text-xl text-nx-dark">Destacadas</h2>
            </div>
            <div className="flex flex-col">
              {secondary.map((a) => <NewsCard key={a.id} article={a} variant="compact" />)}
            </div>
          </aside>
        </div>
      </section>

      {/* Values strip */}
      <section className="bg-nx-dark text-white">
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-2 lg:grid-cols-4 gap-6">
          {VALUES.map((v, i) => {
            const Icon = v.icon;
            return (
              <div key={i} className="flex gap-3">
                <Icon className="text-nx-orange shrink-0 mt-0.5" size={22} strokeWidth={1.6} />
                <div>
                  <h3 className="font-bold text-sm">{v.title}</h3>
                  <p className="text-xs text-slate-400 mt-1 leading-relaxed">{v.text}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Últimas noticias */}
      <section className="max-w-container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <SectionHeader title="Últimas noticias" color="#FF6B35" to="/actualidad" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-10">
          {latest.map((a) => <NewsCard key={a.id} article={a} variant="grid" />)}
        </div>
      </section>

      {/* Category blocks */}
      {["actualidad", "culturas", "historias", "sociedad", "mundo"].map((cat) => {
        const items = data[cat] || [];
        const meta = CATEGORY_META[cat];
        if (!items.length) return null;
        return (
          <section key={cat} className={`py-12 ${["culturas", "sociedad"].includes(cat) ? "bg-nx-soft" : ""}`}>
            <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
              <SectionHeader title={meta.label} color={meta.color} to={`/${cat}`} subtitle={meta.tagline} />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-10">
                {items.map((a) => <NewsCard key={a.id} article={a} variant="grid" />)}
              </div>
            </div>
          </section>
        );
      })}

      {/* VOCES Y COMUNIDADES */}
      <section className="bg-nx-dark text-white py-16">
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-2">
            <span className="w-1.5 h-7 bg-nx-violet rounded-sm" />
            <h2 className="font-serif font-black text-2xl md:text-3xl">Voces y comunidades</h2>
          </div>
          <p className="text-slate-400 text-sm ml-4.5 mb-8 max-w-2xl">Entrevistas y testimonios de integrantes de comunidades, colectividades, especialistas y referentes culturales, sociales y religiosos.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {(voces.length ? voces : (data.historias || []).slice(0, 3)).map((a) => (
              <Link key={a.id} to={`/${a.category}/${a.slug}`} data-testid={`voces-${a.slug}`}
                className="group relative overflow-hidden rounded-md h-72">
                <img src={a.image} alt={a.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                <div className="absolute bottom-0 p-5">
                  <span className="text-[10px] font-bold tracking-widest uppercase text-nx-violet">Testimonio</span>
                  <h3 className="font-serif font-bold text-lg mt-1 leading-snug">{a.title}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* NEXODUM INVESTIGA */}
      <section className="max-w-container mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex items-center gap-3 mb-2">
          <span className="w-1.5 h-7 bg-nx-orange rounded-sm" />
          <h2 className="font-serif font-black text-2xl md:text-3xl text-nx-dark">NEXODUM Investiga</h2>
        </div>
        <p className="text-gray-500 text-sm ml-4.5 mb-8 max-w-2xl">Informes, investigaciones, conflictos, derechos humanos y trabajos periodísticos especiales.</p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {(investiga.length ? investiga : (data.especiales || [])).slice(0, 4).map((a, idx) => (
            <Link key={a.id} to={`/${a.category}/${a.slug}`} data-testid={`investiga-${a.slug}`}
              className={`group relative overflow-hidden rounded-md ${idx === 0 ? "lg:row-span-2 h-80 lg:h-full min-h-[320px]" : "h-52"}`}>
              <img src={a.image} alt={a.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
              <div className="absolute bottom-0 p-6 text-white">
                <span className="text-[10px] font-bold tracking-widest uppercase bg-nx-orange px-2 py-1">Investigación</span>
                <h3 className={`font-serif font-bold mt-3 leading-tight ${idx === 0 ? "text-2xl md:text-3xl" : "text-lg"}`}>{a.title}</h3>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* NEXODUM MULTIMEDIA */}
      <section className="bg-nx-soft py-16">
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader title="NEXODUM Multimedia" color="#0055FF" to="/multimedia" subtitle="Videos, entrevistas, podcasts, documentales y transmisiones." />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {(data.multimedia || []).map((a) => (
              <Link key={a.id} to={`/${a.category}/${a.slug}`} data-testid={`multimedia-${a.slug}`} className="group">
                <div className="relative overflow-hidden rounded-md aspect-video mb-3">
                  <img src={a.image} alt={a.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-black/25 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                    <PlayCircle size={44} className="text-white/90" strokeWidth={1.3} />
                  </div>
                </div>
                <CategoryBadge category="multimedia" className="mb-2" />
                <h3 className="font-serif font-bold text-base leading-snug text-nx-dark group-hover:text-nx-blue transition-colors line-clamp-2">{a.title}</h3>
                <p className="text-xs text-gray-500 mt-1">{formatDate(a.date)}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Opinion teaser */}
      <section className="max-w-container mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <SectionHeader title="Opinión" color="#0055FF" to="/opinion" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {(data.opinion || []).map((a) => (
            <Link key={a.id} to={`/${a.category}/${a.slug}`} data-testid={`opinion-home-${a.slug}`}
              className="group border-l-2 border-nx-blue pl-4">
              <div className="flex items-center gap-2.5 mb-3">
                {a.author_avatar && <img src={a.author_avatar} alt={a.author} className="w-9 h-9 rounded-full object-cover" />}
                <div>
                  <p className="text-xs font-bold text-nx-dark leading-tight">{a.author}</p>
                  <p className="text-[10px] uppercase tracking-wider text-nx-blue">Opinión</p>
                </div>
              </div>
              <h3 className="font-serif font-bold text-lg leading-snug text-nx-dark group-hover:text-nx-blue transition-colors">{a.title}</h3>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
